/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { API, Auth } from 'aws-amplify';

import { getUserByCognitoUserId } from '../../graphql/queries';
import { AWSAuthMode, AuthHeader } from '../Constants/App';
import { IGraphQLResponse } from '../Models';
import { getWebInfo, signedFetch } from '../Utilities/utils';
import { UserAuthEventInput } from '../../API';
import { createUserAuthEvent } from '../../graphql/mutations';
import awsConfig from '../../Configuration/environment-config';
import { v4 as uuidv4 } from 'uuid';

export class AuthenticationService {
  static async getUserByCognitoUserId(cognitoUserId: string) {
    try {
      const authHeaders: AuthHeader = await this.getAuthHeaders();
      const userByCognitoUserIdResponse: IGraphQLResponse = await API.graphql(
        {
          query: getUserByCognitoUserId,
          variables: { cognitoUserId },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: userByCognitoUserIdResponse?.data?.getUserByCognitoUserId ?? {}, errors: [] };
    } catch (e: any) {
      return { data: {}, errors: e?.errors ?? [] };
    }
  }

  static async getAuthHeaders() {
    const session = await Auth.currentSession();
    return {
      Authorization: session.getIdToken().getJwtToken(),
    };
  }

  static async createUserAuthEvent(userAuthEventInput: UserAuthEventInput) {
    try {
      const authHeaders = await AuthenticationService.getAuthHeaders();
      const createUserAuthEventResponse: IGraphQLResponse = await API.graphql(
        {
          query: createUserAuthEvent,
          variables: { userAuthEventInput },
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: createUserAuthEventResponse?.data?.createUserAuthEvent ?? {}, errors: [] };
    } catch (e: any) {
      return { data: {}, errors: e?.errors ?? [] };
    }
  }

  static async updateCustomUserId(user: object, userId: string): Promise<boolean> {
    if (!userId) return false;
    try {
      await Auth.updateUserAttributes(user, {
        'custom:UserId': userId,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  static async fetchAllProcesses(userId: string, orgId: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getAllProcesses');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        abnHeaders: {
          userid: userId,
          orgid: orgId,
          devicetype: 'D',
          userlanguage: 'en',
        },
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      console.error(`Error occured while fetching processes`, e);
    }
  }
}
